/**
 * plateComponents — "This plate is for:" (Nick, 2026-10-07: "we need our data plates connected to the
 * proper component in our generators data").
 *
 * Pure helpers for the plate-to-component control: the components a plate can be for (the unit-record
 * registry's, in its order: generator, engine, alternator, ats, ats2, then fire pump, controller, other),
 * what we ALREADY HOLD for each one on this unit (make / model / serial, so the person can compare against
 * the photo), and whether the unit already uses a plate photo for it.
 *
 * Where each identity lives: generator / engine / alternator / ats / ats2 are AppSheet Generators columns;
 * fire pump / controller / other are unit-record fields (`_opgUnit.components.<type>.make|model|serial`).
 * The panel only DISPLAYS these; every write goes through the server.
 */
import { UNIT_COMPONENT_TYPES, UNIT_COMPONENTS, isPlateRow, type UnitComponentType } from '@officehub/shared';
import type { GeneratorPhoto } from '../../types/fieldService/generatorPhotos';

export type PlateComponentChoice = UnitComponentType | 'unknown';

export interface HeldIdentity {
  make: string;
  model: string;
  serial: string;
}

const COLUMNS: Readonly<Partial<Record<UnitComponentType, readonly [string, string, string]>>> = {
  generator: ['Generator Make', 'Generator Model', 'Generator Serial Number'],
  engine: ['Engine Make', 'Engine Model', 'Engine Serial Number'],
  alternator: ['Alternator Make', 'Alternator Model', 'Alternator Serial Number'],
  ats: ['ATS Make', 'ATS Model', 'ATS Serial Number'],
  ats2: ['ATS 2 Make', 'ATS 2 Model', 'ATS 2 Serial Number'],
};

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const text = (v: unknown): string => (typeof v === 'string' || typeof v === 'number' ? String(v).trim() : '');

function storedValue(node: unknown): string {
  return isRecord(node) && node.cleared !== true ? text(node.value) : '';
}

function componentNode(generator: unknown, type: UnitComponentType): Record<string, unknown> | null {
  if (!isRecord(generator) || !isRecord(generator._opgUnit)) return null;
  const components = generator._opgUnit.components;
  return isRecord(components) && isRecord(components[type]) ? (components[type] as Record<string, unknown>) : null;
}

/** What the unit record / Generators columns hold for this component's identity. Blank strings when unknown. */
export function heldIdentity(generator: unknown, type: UnitComponentType): HeldIdentity {
  const cols = COLUMNS[type];
  if (cols && isRecord(generator)) {
    return { make: text(generator[cols[0]]), model: text(generator[cols[1]]), serial: text(generator[cols[2]]) };
  }
  const node = componentNode(generator, type);
  return { make: storedValue(node?.make), model: storedValue(node?.model), serial: storedValue(node?.serial) };
}

/** True when the unit already uses a plate photo for this component. */
export function holdsPlatePhoto(generator: unknown, type: UnitComponentType): boolean {
  return storedValue(componentNode(generator, type)?.photo) !== '';
}

export interface PlateChoiceRow {
  key: UnitComponentType;
  label: string;
  held: HeldIdentity;
  hasPhoto: boolean;
}

/** The component rows for the panel, in registry order. */
export function plateChoiceRows(generator: unknown): PlateChoiceRow[] {
  return UNIT_COMPONENT_TYPES.map((key) => ({
    key,
    label: UNIT_COMPONENTS[key].label,
    held: heldIdentity(generator, key),
    hasPhoto: holdsPlatePhoto(generator, key),
  }));
}

/**
 * Is this photo a data plate? The ONE rule (isPlateRow, shared/src/lib/plateProof/plateRow.ts) that the server's confirm, proof read
 * and set-component services use: a person's category decides; else the AI's data-plate; else a report-time (capture) label from a
 * plate field, which a person's relabel carries forward, unless a confident classifier (>= 0.9) called it something else.
 */
export function isPlatePhoto(photo: Pick<GeneratorPhoto, 'category' | 'ai' | 'componentSource' | 'componentEvidence'>): boolean {
  return isPlateRow(photo);
}

/**
 * May the server be asked what the report put this photo under? Only a photo that came from a plate field of a report and has no
 * label of any kind yet (a person's, the starter's or the report's own at capture all stand).
 */
export function captureSuggestionEligible(photo: Pick<GeneratorPhoto, 'component' | 'componentSource' | 'sourceRef'>): boolean {
  if (photo.component != null || photo.componentSource != null) return false;
  const key = (photo.sourceRef as { fieldKey?: unknown } | null | undefined)?.fieldKey;
  return key === 'componentSpecs' || key === 'unitDataPlatePhoto';
}

/** Only a data plate that is not removed can be tied to a component. */
export function canTieToComponent(
  photo: Pick<GeneratorPhoto, 'category' | 'ai' | 'state' | 'componentSource' | 'componentEvidence'>
): boolean {
  return isPlatePhoto(photo) && photo.state !== 'hidden';
}

/** Short text for the toolbar button. */
export function plateLabelText(photo: Pick<GeneratorPhoto, 'component'>): string {
  if (!photo.component) return 'Tie plate to a component';
  if (photo.component === 'unknown') return 'Plate: not sure';
  const def = (UNIT_COMPONENTS as Record<string, { label: string } | undefined>)[photo.component];
  return `Plate: ${def?.label ?? photo.component}`;
}
